import { Badge } from './ui/badge';
import { Clock, CheckCircle2, AlertTriangle, FileEdit } from 'lucide-react';

/**
 * Unified status badge for a SocialPost.
 * Reads `status` (draft|scheduled|published|failed) and falls back to
 * `publish_status` for finer-grained display.
 *
 * Variants:
 *  - draft     → zinc neutral
 *  - scheduled → amber + scheduled-for time
 *  - published → emerald + check
 *  - failed    → red + alert (with publish_error tooltip)
 */
export const PostStatusBadge = ({ post, size = 'md', showTime = true, className = '' }) => {
  if (!post) return null;
  const status = post.status || 'draft';
  const sizeCls = size === 'sm' ? 'text-[10px] px-1.5 py-0' : 'text-xs';

  if (status === 'published') {
    return (
      <Badge
        variant="outline"
        className={`bg-emerald-50 text-emerald-700 border-emerald-200 inline-flex items-center gap-1 ${sizeCls} ${className}`}
        data-testid="post-status-published"
      >
        <CheckCircle2 className="w-3 h-3" />
        Published
      </Badge>
    );
  }

  if (status === 'failed') {
    return (
      <Badge
        variant="outline"
        className={`bg-red-50 text-red-700 border-red-200 inline-flex items-center gap-1 ${sizeCls} ${className}`}
        title={post.publish_error || 'Publish failed'}
        data-testid="post-status-failed"
      >
        <AlertTriangle className="w-3 h-3" />
        Failed
      </Badge>
    );
  }

  if (status === 'scheduled' && post.scheduled_for) {
    return (
      <Badge
        variant="outline"
        className={`bg-amber-50 text-amber-700 border-amber-200 inline-flex items-center gap-1 ${sizeCls} ${className}`}
        data-testid="post-status-scheduled"
      >
        <Clock className="w-3 h-3" />
        {showTime
          ? new Date(post.scheduled_for).toLocaleString([], {
              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
            })
          : 'Scheduled'}
      </Badge>
    );
  }

  return (
    <Badge
      variant="outline"
      className={`bg-zinc-50 text-zinc-600 border-zinc-200 inline-flex items-center gap-1 ${sizeCls} ${className}`}
      data-testid="post-status-draft"
    >
      <FileEdit className="w-3 h-3" />
      Draft
    </Badge>
  );
};

export default PostStatusBadge;
